import { useCallback, useEffect, useMemo, useState } from "react";

import { gql } from "@apollo/client";

import { ToggleField } from "@/components/pieces/form/ToggleField";
import { useGetCoachTimeslotsQuery } from "@/graphql";
import { formatDate, getBrowserTimezone } from "@/utils/datetime";

import { canViewAllTimes } from "./can-view-all-times";
import { SideBySideTimeslotLayout } from "./SideBySideTimeslotLayout";
import { StackedTimeslotLayout } from "./StackedTimeslotLayout";

export const GET_COACH_TIMESLOTS = gql`
  query GetCoachTimeslots(
    $coachId: String!
    $productId: String!
    $showAllTimes: Boolean!
    $start: DateTime
    $end: DateTime
    $timeZone: String
  ) {
    timeslots(
      coachId: $coachId
      productId: $productId
      showAllTimes: $showAllTimes
      start: $start
      end: $end
      timeZone: $timeZone
    ) {
      available
      unavailable
    }
  }
`;

export interface TimeslotCardProps {
  coachId: string;
  productId?: string;
  requiresBookingApproval: boolean;
  onSelectTimeslots: (timeslots: string[]) => void;
  selectedTimeslots: string[];
  showAllTimes: boolean;
  setShowAllTimes: (showAllTimes: boolean) => void;
  isViewOnly: boolean;
  variant: "stacked" | "side-by-side";
  start?: string | null;
  end?: string | null;
  preferredTimeslots: string[];
  onPreferredChange: (preferredTimeslots: string[]) => void;
}

export function TimeslotCard({
  coachId,
  productId,
  requiresBookingApproval,
  onSelectTimeslots,
  selectedTimeslots,
  showAllTimes,
  setShowAllTimes,
  isViewOnly,
  start,
  end,
  variant,
  preferredTimeslots,
  onPreferredChange,
}: TimeslotCardProps) {
  const [dateString, setDateString] = useState<string>(
    formatDate(new Date(), "MM-dd-yyyy", getBrowserTimezone()) || "",
  );
  const [timezone, setTimezone] = useState<string>(getBrowserTimezone());

  const { data, loading } = useGetCoachTimeslotsQuery({
    variables: {
      coachId,
      productId: productId || "",
      showAllTimes,
      start,
      end,
      timeZone: timezone,
    },
    skip: !productId,
  });

  const timeslots = data?.timeslots.available || [];
  const unavailableTimeslots = data?.timeslots.unavailable || [];

  const availableDates = useMemo(
    () =>
      [...timeslots, ...unavailableTimeslots].reduce(
        (res: Record<string, boolean>, timeslot) => {
          const day = formatDate(timeslot, "MM-dd-yyyy", timezone);
          res[day || ""] = true;
          return res;
        },
        {},
      ),
    [timeslots, unavailableTimeslots, timezone],
  );

  useEffect(() => {
    const date = Object.keys(availableDates)[0];
    if (date) {
      setDateString(date);
    }
  }, [availableDates]);

  const filterTimeslots = useCallback(
    (timeslots: string[]) => {
      if (!dateString) return [];
      return timeslots.filter((timeslot) => {
        const formattedTimeslot = formatDate(timeslot, "MM-dd-yyyy", timezone);
        return dateString === formattedTimeslot;
      });
    },
    [dateString, timezone],
  );

  const filteredTimeslots = useMemo(
    () => filterTimeslots(timeslots),
    [timeslots, dateString, timezone],
  );

  const filteredUnavailableTimeslots = useMemo(
    () => filterTimeslots(unavailableTimeslots),
    [unavailableTimeslots, dateString, timezone],
  );

  return (
    <>
      {requiresBookingApproval && (
        <div className="mb-4 rounded-lg bg-blue-50 p-3 text-sm text-blue-700">
          <div>Select times that work for you</div>
          <div className="font-medium">
            The coach will choose from your selected times
          </div>
        </div>
      )}
      {variant === "side-by-side" ? (
        <SideBySideTimeslotLayout
          requiresBookingApproval={requiresBookingApproval}
          timeslots={filteredTimeslots}
          unavailableTimeslots={filteredUnavailableTimeslots}
          availableDates={availableDates}
          loading={loading}
          isViewOnly={isViewOnly}
          dateString={dateString}
          setDateString={setDateString}
          timezone={timezone}
          setTimezone={setTimezone}
          onSelectTimeslots={onSelectTimeslots}
          selectedTimeslots={selectedTimeslots}
        />
      ) : (
        <StackedTimeslotLayout
          requiresBookingApproval={requiresBookingApproval}
          timeslots={filteredTimeslots}
          unavailableTimeslots={filteredUnavailableTimeslots}
          availableDates={availableDates}
          loading={loading}
          isViewOnly={isViewOnly}
          dateString={dateString}
          setDateString={setDateString}
          timezone={timezone}
          setTimezone={setTimezone}
          onSelectTimeslots={onSelectTimeslots}
          selectedTimeslots={selectedTimeslots}
          preferredTimeslots={preferredTimeslots}
          onPreferredChange={onPreferredChange}
        />
      )}
      {dateString && canViewAllTimes(coachId) && (
        <div className="mb-2 mt-4 flex items-center">
          <ToggleField
            checked={showAllTimes}
            onChange={() => setShowAllTimes(!showAllTimes)}
            size={10}
          />
          <div className="ml-2 text-sm">Show All Times</div>
        </div>
      )}
    </>
  );
}
